"use client";

/**
 * 画面で台帳（ui-texts.ts）の文を読む。管理UIの「文言」で書き換えた文（公開版）を /api/ui-texts から一度だけ読み、端末にも覚えておく。
 * 書き換えていない文は台帳の既定の文
 */
import { useCallback, useSyncExternalStore } from "react";
import { UI_TEXTS, fillUiText, parseUiTexts, type UiTextKey, type UiTextOverrides } from "@/lib/ui-texts";

const CACHE_KEY = "uiTexts";
const EMPTY: UiTextOverrides = {};

let current: UiTextOverrides | null = null;
let fetched = false;
const listeners = new Set<() => void>();

function read(): UiTextOverrides {
  if (current) return current;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    current = raw ? parseUiTexts({ texts: JSON.parse(raw) }) : EMPTY;
  } catch {
    current = EMPTY;
  }
  return current;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!fetched) {
    fetched = true;
    fetch("/api/ui-texts")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { texts?: unknown } | null) => {
        const next = parseUiTexts(data ?? undefined);
        const changed = JSON.stringify(next) !== JSON.stringify(read());
        current = next;
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(next));
        } catch {}
        if (changed) listeners.forEach((l) => l());
      })
      .catch(() => {});
  }
  return () => listeners.delete(listener);
}

/**
 * t(名前, 差し込み) で文を得る。raw(名前) は差し込みを入れる前の文（文の一部だけ字を変えるときに使う）
 */
export function useUiTexts() {
  const overrides = useSyncExternalStore(subscribe, read, () => EMPTY);
  const raw = useCallback((key: UiTextKey): string => overrides[key] ?? UI_TEXTS[key].text, [overrides]);
  const t = useCallback((key: UiTextKey, vars?: Record<string, string | number>) => fillUiText(raw(key), vars), [raw]);
  return { t, raw };
}
